import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';

import type { ApiError } from '@/api/client.types';

import type { GradebookRoster } from './gradebookTypes';
import { useKuduGradebookService } from './gradebookService';

export const rosterQueryKey = (courseId: string) =>
  ['gradebook', 'roster', courseId] as const;

interface UseRosterOptions {
  enabled?: boolean;
}

export function useRoster(
  courseId: string | undefined,
  options: UseRosterOptions = {},
): UseQueryResult<GradebookRoster, ApiError> {
  const service = useKuduGradebookService();
  const enabled = options.enabled ?? true;

  return useQuery<GradebookRoster, ApiError>({
    enabled: enabled && Boolean(courseId) && Boolean(service),
    queryFn: async () => {
      if (!courseId || !service) {
        const error = new Error('Roster service is unavailable.') as ApiError;
        error.kind = 'data';
        throw error;
      }

      return service.getRoster(courseId);
    },
    queryKey: rosterQueryKey(courseId ?? 'missing-course'),
    staleTime: 60_000,
  } satisfies UseQueryOptions<GradebookRoster, ApiError>);
}
